import styled, { StyledComponentPropsWithRef } from 'styled-components'

import { theme } from 'styles/theme'
import { withSvgIconClassName, type MaybeWithSize } from '../Icon/Icon'
import { HiddenText } from '../Typography/Typography'

import type { ComponentProps } from 'react'

const SpinnerElement = styled.div`
  position: relative;
  overflow: hidden;
  /* increase specificity to force margin:auto over the margin written in 'withSvgIconClassName' */
  &[class] {
    margin: auto;
  }
  /* Sizes override min- sizes */
  block-size: var(--size, 1em);
  inline-size: var(--size, 1em);
  ::before,
  ::after {
    content: '';
    background-color: ${theme.colors.White};
  }
  ::before {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: calc(var(--size, 1em) / 4) solid var(--color, currentColor);
  }
  @keyframes spin-opacity-overlay-square {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  ::after {
    display: block;
    border-start-end-radius: 100%;
    opacity: 0.7;
    inline-size: 50%;
    block-size: 50%;
    position: relative;
    inset-inline-start: 50%;
    transform-origin: bottom left;
    animation: spin-opacity-overlay-square infinite 2s linear;
  }
`

interface SpinnerIconProps extends Omit<StyledComponentPropsWithRef<typeof SpinnerElement>, 'title'>, WithTitle, MaybeWithSize {}

// Wrapped in styled so we can interpolate it in other styled components
export const SpinnerIcon = styled(
  ({ children, size = 3, color = theme.colors.MainYellow, style = {}, ...props }: SpinnerIconProps) => {
    return (
      <SpinnerElement
        {...withSvgIconClassName({
          style: { ...style, '--color': color, '--size': typeof size === 'number' ? `${size}em` : size },
          ...props,
        })}
      >
        {children}
        <HiddenText {...{ children: props.title }} />
      </SpinnerElement>
    )
  },
)``

export type SpinnerIconType = typeof SpinnerIcon

interface WithIsSpinning {
  isSpinning: boolean
}

// While we don't normally use HTML tags without content, this is the best solution I found.
// The problem is that SpinnerIcon needs to have a smaller size than the container.
const SpinnerBackdrop = styled.span`
  background-color: ${theme.colors.White};
`

const SpinnerWrapper = styled.div<WithIsSpinning>`
  gap: 2em;
  display: flex;
  flex-direction: column;

  flex-grow: 1;

  inline-size: 100%;
  position: relative;
  ${SpinnerIcon}, ${SpinnerBackdrop} {
    position: absolute;
    inset: 0;
    display: ${({ isSpinning }) => (isSpinning ? 'initial' : 'none')};
  }
`

export interface SpinnerProps
  extends Omit<ComponentProps<typeof SpinnerWrapper>, 'title'>,
    MaybeWithSize,
    MaybeWithTitle {
  spinnerTitle: string
}

export const Spinner = ({ children, spinnerTitle: title, size = '25vmin', ...props }: SpinnerProps) => (
  <SpinnerWrapper {...{isSpinning: true, ...props}}>
    {children}
    <SpinnerBackdrop {...{ title }} />
    <SpinnerIcon {...{ size, title }} />
  </SpinnerWrapper>
)

export type SpinnerType = typeof Spinner
